import React, { useCallback, useEffect, useRef, useState } from "react";
import { useSelector } from "react-redux";
import { Box } from "@material-ui/core";
import {
  BasicTable,
  CustomPagination,
  CustomTableHeader,
  NoDataFound,
  TableSkeleton,
} from "../../../../../Components";
import { useVerticalNav } from "../../../../../Contexts/VerticalNavContext";
import { OfferTableHeaderData } from "./OfferTableHeaderData";
import { GetFilteredVendorClientTrackingsAPI } from "../../../../../Services";
import { useLayoutDetection, useTranslate } from "../../../../../Hooks";

import useStyles from "./styles";

function OffersPanel({ contactId, setRefetchOffers, refetch }) {
  const styles = useStyles();
  const [data, setData] = useState({
    result: [],
    totalCount: 0,
  });
  const { isDarkMode } = useSelector((state) => state.theme);

  const { translate } = useTranslate("AfterSalesClientProfileView");
  const [isLoading, setIsLoading] = useState(false);
  const { setStickyBottomBox } = useVerticalNav();
  const dropPagination = useRef(null);
  const stickyPagination = useRef(null);
  const { isNewLayout: isReskinningLayout, hasNewLayout } =
    useLayoutDetection();
  const isNewLayout = isReskinningLayout && hasNewLayout;

  const [pagination, setPagination] = useState({ pageIndex: 1, pageSize: 25 });
  const [inputValue, setInputValue] = useState(pagination.pageIndex);

  const getFilteredVendorClientTrackings = useCallback(async () => {
    try {
      setIsLoading(true);
      const response = await GetFilteredVendorClientTrackingsAPI({
        contactId,
        pageIndex: pagination.pageIndex,
        pageSize: pagination.pageSize,
      });

      setData(response);
    } catch (error) {
      //   showError(translate("something_went_wrong"));
    } finally {
      setIsLoading(false);
    }
  }, [contactId]);

  useEffect(() => {
    getFilteredVendorClientTrackings();
  }, [contactId]);

  useEffect(() => {
    if (refetch) getFilteredVendorClientTrackings();
    setRefetchOffers(false);
  }, [refetch]);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (!stickyPagination.current) return;
          if (entry.isIntersecting) {
            stickyPagination.current.style.display = "none";
          } else {
            stickyPagination.current.style.display = "block";
          }
        });
      },
      {
        root: null,
        threshold: 1,
      }
    );

    if (dropPagination.current) {
      observer.observe(dropPagination.current);
    }

    return () => {
      if (dropPagination.current) {
        observer.unobserve(dropPagination.current);
      }
    };
  }, []);

  useEffect(() => {
    if (isNewLayout) {
      setStickyBottomBox(
        <CustomPagination
          hideInMobile
          ref={stickyPagination}
          currentPage={pagination.pageIndex}
          inputValue={inputValue}
          setInputValue={setInputValue}
          totalItems={data.totalCount}
          itemsPerPage={pagination.pageSize}
          onPageChange={(page) =>
            setPagination((prev) => ({ ...prev, pageIndex: page }))
          }
          onItemsPerPageChange={(items) =>
            setPagination((prev) => ({ ...prev, pageSize: items }))
          }
          isLoading={isLoading}
          isSticky
        />
      );
    }

    return () => {
      if (isNewLayout) {
        setStickyBottomBox(null);
      }
    };
  }, [isLoading, isNewLayout]);

  return (
    <div>
      {isLoading ? (
        <TableSkeleton rowsNum={6} />
      ) : (
        <Box className={styles.tableContainer}>
          <BasicTable
            rowHeight={84}
            rowsData={
              data?.result?.map((item, idx) => {
                return {
                  ...item,
                  id: idx,
                };
              }) || []
            }
            columns={OfferTableHeaderData({ isDarkMode }).map((column) => ({
              ...column,
              renderHeader: (params) => (
                <CustomTableHeader
                  params={params}
                  title={column.headerName}
                  hideSelect
                  isHiddenFilter
                />
              ),
            }))}
            emptyState={{ NoRowsOverlay: () => <div></div> }}
          />
          {!isLoading && data?.totalCount === 0 && (
            <NoDataFound title={translate("no-offers-linked")} subTitle={""} />
          )}
          <div ref={dropPagination}>
            <CustomPagination
              totalItems={data?.totalCount}
              itemsPerPage={pagination.pageSize}
              currentPage={pagination.pageIndex}
              inputValue={inputValue}
              setInputValue={setInputValue}
              onPageChange={(page) =>
                setPagination((prev) => ({ ...prev, pageIndex: page }))
              }
              onItemsPerPageChange={(items) =>
                setPagination((prev) => ({ ...prev, pageSize: items }))
              }
              isLoading={isLoading}
            />
          </div>
        </Box>
      )}
    </div>
  );
}

export default OffersPanel;
